import { Box, FormControl, FormErrorMessage, FormLabel, IconButton, Input, InputGroup, InputRightElement, Tag } from '@chakra-ui/react';
import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { FaAngleDown } from 'react-icons/fa6';
import { MdClose } from 'react-icons/md';

import { useAlert } from '../../hooks/use-alert';
import { Dropdown } from './dropdown';
import { FieldComponentProps } from './types';

type SelectFieldProps = FieldComponentProps<EntityBase> & {
  name?: string;
  options?: EntityBase[];
  field?: string;
  onChangeValue?: (id: string) => void;
  onFetchOptions?: (name: string) => Promise<EntityBase[]>;
  loading?: boolean;
  clearable?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  customBodyRender?: (value: EntityBase) => ReactNode;
};

export const SelectField: FC<SelectFieldProps> = (props) => {
  const {
    id,
    name,
    label,
    disabled,
    value,
    field = 'name',
    onChange,
    onChangeValue,
    onFetchOptions,
    loading,
    error,
    helperText,
    required,
    size = 'md',
    clearable = true,
    readOnly = false,
  } = props;
  const [open, setOpen] = useState(false);
  const fieldRef = useRef<HTMLDivElement>(null);
  const [options, setOptions] = useState<EntityBase[]>(
    props.options || [...(value ? [value] : [])]
  );
  const [isFetchDone, setIsFetchDone] = useState<boolean>(false);

  useEffect(() => {
    // if (options.length === 0)
    setOptions(props.options || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.options]);

  useEffect(() => {
    if (options.length === 0 && props.value) {setOptions([props.value]);}
  }, [options.length, props.value]);

  const alert = useAlert();

  const onFocus = () => {
    if (readOnly) {return;}
    !disabled && setOpen(true);
    if (!isFetchDone) {
      try {
        onFetchOptions && onFetchOptions(name).then(setOptions);
        setIsFetchDone(true);
      } catch (e) {
        console.log(e);
        alert.error('Ошибка при получении списка значений');
      }
    }
  };

  const handleMouseDown = () => {
    !disabled && !readOnly && setOpen(!open);
  };

  const handleClickOutside = () => {
    document.removeEventListener('mousedown', handleClickOutside);
    setOpen(false);
  };

  const handleMouseLeave = () => {
    document.addEventListener('mousedown', handleClickOutside);
  };

  const handleMouseEnter = () => {
    document.removeEventListener('mousedown', handleClickOutside);
  };

  const isSelected = (option: EntityBase) => {
    return (
      option?.id === value?.id
    );
  };

  const handleOptionSelect = (option: EntityBase) => {
    setOpen(false);
    onChange?.(option);
    onChangeValue?.(option?.id.toString());
  };

  const handleClear = () => {
    if (!clearable) {
      return;
    }
    onChange?.(undefined);
    onChangeValue?.('');
  };

  return (<InputGroup size={size}>
    <FormControl 
      variant="floating" 
      id={id} 
      isRequired={required}
      isInvalid={error}
      isDisabled={disabled}
      ref={fieldRef}
      onMouseLeave={handleMouseLeave}
      onMouseEnter={handleMouseEnter}
    >
      <InputRightElement pr={clearable && !disabled ? 6 : 0}>
        {clearable && !disabled && <IconButton icon={<MdClose />} aria-label="clear" 
          backgroundColor="transparent" 
          borderRadius="50%"
          size="xs"
          onClick={handleClear}
        />}
        <IconButton icon={<FaAngleDown />} aria-label="clear" 
          backgroundColor="transparent" 
          borderRadius="50%"
          size="xs"
          onClick={handleMouseDown}
          transform={open ?  'rotate(180deg)' : 'rotate(0deg)'}
        />
      </InputRightElement>
      <Dropdown 
        fieldRef={fieldRef}
        open={open}
        setOpen={setOpen}
      >
        {loading && (
          <Box  mx={4} my={2} textAlign='center'>
            Загрузка...
          </Box>
        )}
        {!loading && options.length === 0 && <Box 
          mx={4} my={2} textAlign='center'
          textColor='gray.400'
        >Пусто</Box>}
        {options?.map((item) => {
          const selected = isSelected(item);
          return <Dropdown.Option
            key={item.id}
            selected={selected}
            onClick={() => handleOptionSelect(item)}
          >
            {props.customBodyRender?.(item) || item?.[field]}
          </Dropdown.Option>;
        })}
      </Dropdown>
      <Input 
        position="relative"
        placeholder={label}
        value={value?.[field] || ''}
        contentEditable={false}
        onFocus={onFocus}
        onClick={() => !readOnly && setOpen(true)}
        size={size}
        paddingEnd={clearable ? 14 : 8}
        isReadOnly
      />
      <FormLabel textColor="dark.300">{label}</FormLabel>
      {error && <FormErrorMessage>{helperText}</FormErrorMessage>}
    </FormControl>
  </InputGroup>);
};

export default SelectField;


